<template>
    <div class="app-activation-info">
        <slot name="txtBefore"></slot>
        <b-form
                inline
                :id="'form' + _uid"
                :novalidate="true"
                data-form-activation-info
        >
            <slot name="txtBeforeActivationMsisdn"></slot>

          <div class="form-row">
            <LabeledTextInput
              name="simnumber"
              data-activation-code
              :model.sync="activationCode"
              :invalid.sync="invalid.activationCode"
              :validation="validations.activationCode"
              :mandatory="true"
              ref="nestedInputSimNumber"
            >
              <template v-for="slot in possibleChildSlotsFor('SimNumber')" v-slot:[`${slot.child}`]>
                <slot :name="`${slot.parent}`"></slot>
              </template>
            </LabeledTextInput>
          </div>

      <div class="input-column">
        <LabeledPhoneNumberInput
          name="msisdn"
          :model.sync="msisdn"
          :withCountryCode="true"
          :mandatory="true"
          :invalid.sync="invalid.msisdn"
          ref="nestedInputMsisdnInput"
        >
          <template v-for="slot in possibleChildSlotsFor('Msisdn')" v-slot:[`${slot.child}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>
        </LabeledPhoneNumberInput>
      </div>
      <div class="btn-column">
        <button @click.prevent.stop="next()" type="button" class="filled yellow" :aria-label="getSlotText('ariaLabelCta','Aktivierung starten')">
            <slot name="btnNext">weiter</slot>
        </button>
      </div>
            <slot name="txtAfterBtnNext"></slot>
        </b-form>
        <slot name="txtAfter"></slot>
    </div>
</template>

<script>
import { BForm } from 'bootstrap-vue'
import { required } from 'vuelidate/lib/validators'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import LabeledTextInput from '@/components/forms/LabeledTextInput.vue'
import processSteps from '@/mixins/processSteps'
import validationFlow from '@/mixins/validators/validationFlow'
import { activationCodeValidator } from '@/mixins/validators/activationCodeValidator'
import preFilledInput from '@/mixins/preFilledInput'
import { stepName as nextStep } from '@/components/checkout/tarifftile/ActivationTariffTileHorizontalWrapper.vue'
import beErrors from '@/mixins/beErrors'
import { splitError } from '@/helper/splitError'
import trackingHandler from '@/mixins/trackingHandler'
import LabeledPhoneNumberInput from '@/components/forms/LabeledPhoneNumberInput.vue'
export const stepName = 'activationInfo'
export default {
  name: 'ActivationInfo',
  props: {
    retryActivation: undefined
  },
  data () {
    return {
      msisdn_: null,
      activationCode_: null,
      displayErrorMessage: false,
      invalidMsisdn: false,
      loading: false,
      invalid: {
        msisdn: null,
        activationCode: null
      },
      validations: {
        msisdn: {
          required
        },
        activationCode: {
          required,
          activationCodeValidator
        }
      }
    }
  },
  components: {
    LabeledPhoneNumberInput,
    BForm,
    LabeledTextInput
  },
  mixins: [
    validationFlow,
    processSteps(stepName, stepName, nextStep), // prev = current, because first step
    preFilledInput('activationCode', 'getActivationCode'),
    preFilledInput('msisdn', 'getMsisdn'),
    beErrors,
    trackingHandler
  ],
  mounted () {
    this.triggerTrackingInfo()
  },
  methods: {
    ...mapMutations('checkout', [
      'goToStep'
    ]),
    ...mapActions('checkout/tariff', [
      'setSelectedTariffToDefault',
      'loadDefaultTariff'
    ]),
    // activation v2
    ...mapActions('checkoutv2/activation', ['loadActivationInfo', 'loadPreactivePromotions']),
    async next (retryActivationConfirm) {
      // retryActivation wird benutzt um trotz erfolgtem confirm wieder eine erneute Aktivierung zu ermöglichen
      let retryActivation = this.retryActivation
      if (typeof retryActivationConfirm === 'boolean') {
        retryActivation = retryActivationConfirm
      }

      this.forceValidationOnNestedInputs()

      if (!this.formInvalid) {
        try {
          this.startLoader()
          this.loading = true

          await this.loadActivationInfo({
            msisdn: this.msisdn,
            activationCode: this.activationCode,
            nextStep: this.nextStep,
            allowNextStep: retryActivation
          })

          await this.loadPreactivePromotions()

          this.loading = false
          this.stopLoader()
          this.msisdn = null
          this.activationCode = null
          this.triggerTrackingSelectMnp()

          const isWebIdentPending = this.$store.getters['checkoutv2/activation/isWebIdentPending']
          this.$emit('web-ident-pending', isWebIdentPending)

          if (!isWebIdentPending || retryActivation) {
            this.goToStep(this.nextStep)
          }
        } catch (error) {
          this.loading = false
          this.stopLoader()
          this.errorToast(this.getErrorMessage(error))
          this.triggerErrorTracking(error)
        }
      } else {
        // ensure live feedback for validation errors
        this.vFlowScrollToFirstElemment()
        const result = await this.vFlowGetFirstErrorElementName()
        this.triggerValidationErrorTracking(result)
      }
    },
    errorMessage (error) {
      const firstMessage = splitError(error)?.[0]
      if (firstMessage && firstMessage.extensions?.category === 'graphql') {
        // technical error message, do not use for customer
        return this.fallbackErrorMessage
      }
      return firstMessage.message
    },
    clearErrors () {
      this.$store.commit('clearMessages', {}, { root: true })
    },
    forceValidationOnNestedInputs () {
      Object.entries(this.$refs).forEach(([key, comp]) => {
        if (/^nestedInput/.test(key)) {
          comp.vflowForceFeedback()
        }
      })
    },
    triggerTrackingInfo () {
      const data = {
        page_levels: [
          'mobility',
          'sim activation'
        ],
        login_status: 'not logged in', // check/set in tracking
        page_type: 'content',
        platform_type: 'browser view'
      }
      this.trackData('pageview', data)
    },
    async triggerTrackingSelectMnp () {
      await this.loadDefaultTariff().then(() => {
        if (this.storedTariff === null) {
          this.setSelectedTariffToDefault()
        }
        let propositions = ''
        if (typeof window.trackingNS?.track?.getPropositions === 'function') {
          const tariffoptionId = this.selectedTariffOptionIds.length > 0 ? this.selectedTariffOptionIds[0] : null
          propositions = window.trackingNS.track.getPropositions(this.mnpData, undefined, undefined, this.defaultTariff, null, tariffoptionId)
        }
        const data = {
          page_levels: [
            'mobility',
            'sim activation',
            'select option'
          ],
          login_status: 'not logged in', // check/set in tracking
          page_type: 'product detail',
          platform_type: 'browser view',
          propositions: propositions
        }
        this.trackData('pageview', data)
      })
    }
  },
  computed: {
    ...mapGetters('checkoutv2/activation', {
      getActivationCode: 'activationCode',
      getMsisdn: 'msisdn'
    }),
    ...mapGetters('checkoutv2/activation/_mnpData', {
      storedMNPActive: 'active',
      storedMNPProvider: 'provider'
    }),
    ...mapGetters('checkoutv2/activation/_cart', {
      selectedTariffOptionIds: 'selectedTariffOptionIds'
    }),
    ...mapGetters('checkout/tariff', {
      storedTariff: 'selectedTariff',
      defaultTariff: 'defaultTariff'
    }),
    mnpData: function () {
      return {
        active: this.storedMNPActive,
        provider: this.storedMNPProvider
      }
    },
    getBackendErrorMessage: function () {
      return this.beErrorGetLastMessage()
    },
    fallbackErrorMessage: function () {
      return this.getSlotText('txtTechnicalError', 'Es ist ein technischer Fehler aufgetreten.')
    },
    formInvalid: function () {
      return Object.entries(this.invalid)
        .map(([key, value]) => value)
        .some((value) => value === true)
    }
  }
}
</script>
